import BaseService from './BaseService';

import {
  AdCopyRun,
  FinalizeItem,
  FinalizeResult,
  GroupInfo,
  StaticDelivery,
  GenerationRow,
  GenerationSimilarity,
  GenerationStatusRow,
  InspoSet,
  ProductAsset,
  ProductSummary,
  StaticAdDetailResponse,
  StaticAdFacets,
  StaticAdFilters,
  StaticAdSummary,
  StaticKeyword,
  StaticPipelineRow,
  StaticPrompt,
  StaticFormat,
  StaticUploadResult,
} from 'pages/StaticAds/types';

// The Static Creative library API (/v1/static-ads).
//
// Read paths swallow errors into null via handleHttpRequest, the house default.
// The write paths deliberately let errors propagate, following
// VideoEditingService: the backend's message is the actionable one ("The source
// image link from Meta has expired — re-import this ad"), and a silent null
// would strand the user with a spinner and no reason.
/** One string the generated image will render, with the type treatment asked for it. */
export interface TextElement {
  role: string;
  text: string;
  style: string | null;
  /** >1 when one string repeats as a pattern (a grid of stamps, a tiled watermark). */
  count: number;
}

class StaticAdsService extends BaseService {
  private basePath = 'v1/static-ads';

  async listAds(
    filters: StaticAdFilters = {},
    { limit = 60, offset = 0 }: { limit?: number; offset?: number } = {},
  ): Promise<{ ads: StaticAdSummary[]; total: number } | null> {
    await this._getAccessToken();
    // Arrays stay arrays: axios serializes them as repeated tag[]= params and
    // Express's qs parser reads them back as one array under `tag`. Coercing
    // them to a string here would send the literal "a,b" as a single tag.
    const params: Record<string, string | number | string[]> = { limit, offset };
    Object.entries(filters).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      if (Array.isArray(value) && !value.length) return;
      params[key] = value as string | number | string[];
    });
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/ads`, { params }),
      'Error listing static ads',
    );
  }

  async getAd(staticAdId: string): Promise<StaticAdDetailResponse | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/ads/${staticAdId}`),
      'Error loading static ad',
    );
  }

  async getPipelineStatus(staticAdIds: string[]): Promise<{ statuses: StaticPipelineRow[] } | null> {
    if (!staticAdIds.length) return { statuses: [] };
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/pipeline-status`, { params: { ids: staticAdIds.join(',') } }),
      'Error polling static ad status',
    );
  }

  async getFacets(): Promise<StaticAdFacets | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/facets`),
      'Error loading static ad filters',
    );
  }

  // ── Keywords ──
  //
  // These are the SAME SCRAPE_KEYWORDS rows the video tool manages, scoped
  // server-side to those with the static flag set.

  async listKeywords(activeOnly = false): Promise<{ keywords: StaticKeyword[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/keywords`, { params: { activeOnly } }),
      'Error listing static keywords',
    );
  }

  async createKeyword(body: Partial<StaticKeyword>): Promise<{ keyword: StaticKeyword }> {
    await this._getAccessToken();
    const resp = await this.http.post(`${this.basePath}/keywords`, body);
    return resp.data;
  }

  async updateKeyword(keywordId: string, body: Partial<StaticKeyword>): Promise<{ keyword: StaticKeyword }> {
    await this._getAccessToken();
    const resp = await this.http.put(`${this.basePath}/keywords/${keywordId}`, body);
    return resp.data;
  }

  /** Clears the static flag. The row survives if the video tool still uses it. */
  async removeKeyword(keywordId: string): Promise<{ removed: boolean; stillUsedByVideo: boolean }> {
    await this._getAccessToken();
    const resp = await this.http.delete(`${this.basePath}/keywords/${keywordId}`);
    return resp.data;
  }

  // ── Followed brands ──

  /** "Check now": queue a fetch of every page of a followed brand, without waiting for the morning run. */
  async fetchFollowedBrand(groupId: string): Promise<{ queued: number }> {
    await this._getAccessToken();
    const resp = await this.http.post(`${this.basePath}/followed-brands/${groupId}/fetch`);
    return resp.data;
  }

  // ── Prompts ──

  async listPrompts(): Promise<{ prompts: StaticPrompt[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/prompts`),
      'Error listing static prompts',
    );
  }

  /**
   * Every tag vocabulary this tool uses. `tags` is AD tags only — the Library
   * filter is built from it, and widening it to the union would offer options
   * that can never match an ad there. `suggestions` is the union, for editors.
   */
  async getTags(): Promise<{
    tags: string[]; generationTags?: string[]; groupTags?: string[]; suggestions?: string[];
  } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/tags`),
      'Error loading static ad tags',
    );
  }

  // ── Writes (errors propagate on purpose) ──

  async uploadImages(
    files: File[],
    fields: Record<string, string> = {},
    onUploadProgress?: (percent: number) => void,
  ): Promise<StaticUploadResult> {
    await this._getAccessToken();
    const form = new FormData();
    files.forEach(file => form.append('files', file));
    Object.entries(fields).forEach(([key, value]) => {
      if (value) form.append(key, value);
    });

    const resp = await this.http.post(`${this.basePath}/ads/upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (event: ProgressEvent) => {
        if (onUploadProgress && event.total) {
          onUploadProgress(Math.round((event.loaded / event.total) * 100));
        }
      },
    });
    return resp.data as StaticUploadResult;
  }

  async triggerScrape(params: {
    keyword?: string;
    keywordId?: string;
    scrapeDepth?: number;
    startDateMin?: string | null;
    startDateMax?: string | null;
  }): Promise<{ scrapeRunId: string; keyword: string; scrapeDepth: number }> {
    await this._getAccessToken();
    const resp = await this.http.post(`${this.basePath}/scrape`, params);
    return resp.data;
  }

  async analyze(staticAdId: string, options: {
    analysisModel?: string;
    promptId?: string;
  } = {}): Promise<{ status: string }> {
    await this._getAccessToken();
    const resp = await this.http.post(`${this.basePath}/ads/${staticAdId}/analyze`, options);
    return resp.data;
  }

  async retry(staticAdId: string): Promise<{ status: string }> {
    await this._getAccessToken();
    const resp = await this.http.post(`${this.basePath}/ads/${staticAdId}/retry`);
    return resp.data;
  }

  async batchAnalyze(
    staticAdIds: string[],
    options: { analysisModel?: string; promptId?: string } = {},
  ): Promise<{ results: Array<{ staticAdId: string; status: string }> }> {
    await this._getAccessToken();
    const resp = await this.http.post(`${this.basePath}/ads/batch-analyze`, { staticAdIds, ...options });
    return resp.data;
  }

  async deleteAd(staticAdId: string): Promise<{ deleted: boolean }> {
    await this._getAccessToken();
    const resp = await this.http.delete(`${this.basePath}/ads/${staticAdId}`);
    return resp.data;
  }

  async addTag(staticAdId: string, tag: string): Promise<{ tags: string[] }> {
    await this._getAccessToken();
    const resp = await this.http.post(`${this.basePath}/ads/${staticAdId}/tags`, { tag });
    return resp.data;
  }

  async removeTag(staticAdId: string, tag: string): Promise<{ tags: string[] }> {
    await this._getAccessToken();
    const resp = await this.http.delete(`${this.basePath}/ads/${staticAdId}/tags/${encodeURIComponent(tag)}`);
    return resp.data;
  }

  async addGenerationTag(generationId: string, tag: string): Promise<{ tags: string[] }> {
    await this._getAccessToken();
    const resp = await this.http.post(`${this.basePath}/generations/${generationId}/tags`, { tag });
    return resp.data;
  }

  async removeGenerationTag(generationId: string, tag: string): Promise<{ tags: string[] }> {
    await this._getAccessToken();
    const resp = await this.http.delete(`${this.basePath}/generations/${generationId}/tags/${encodeURIComponent(tag)}`);
    return resp.data;
  }

  async addGroupTag(groupId: string, tag: string): Promise<{ tags: string[] }> {
    await this._getAccessToken();
    const resp = await this.http.post(`${this.basePath}/generation-groups/${groupId}/tags`, { tag });
    return resp.data;
  }

  async removeGroupTag(groupId: string, tag: string): Promise<{ tags: string[] }> {
    await this._getAccessToken();
    const resp = await this.http.delete(`${this.basePath}/generation-groups/${groupId}/tags/${encodeURIComponent(tag)}`);
    return resp.data;
  }

  /** Rename one creative. An empty name clears it back to the derived default. */
  async saveGenerationName(generationId: string, name: string): Promise<{ name: string | null }> {
    await this._getAccessToken();
    const resp = await this.http.put(`${this.basePath}/generations/${generationId}/name`, { name });
    return resp.data;
  }

  async saveGroupName(groupId: string, name: string): Promise<{ name: string | null }> {
    await this._getAccessToken();
    const resp = await this.http.put(`${this.basePath}/generation-groups/${groupId}/name`, { name });
    return resp.data;
  }

  /** The Ad Library URL these keyword settings would search — built server-side. */
  async keywordSearchUrl(body: {
    keyword: string;
    dateRangePreset?: string;
    startDateMin?: string | null;
    startDateMax?: string | null;
    pageIds?: string[];
    pageGroupIds?: string[];
  }): Promise<{ url: string; advertiserCount: number } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.post(`${this.basePath}/keyword-search-url`, body),
      'Could not build the Ad Library link',
    );
  }

  // ── Product catalog ──

  async listProducts(store?: string): Promise<{ products: ProductSummary[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/products`, { params: store ? { store } : {} }),
      'Error listing products',
    );
  }

  async listProductAssets(productId: number): Promise<{ assets: ProductAsset[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/products/${productId}/assets`),
      'Error listing product photos',
    );
  }

  async uploadProductAssets(productId: number, files: File[]): Promise<{ created: number }> {
    await this._getAccessToken();
    const form = new FormData();
    files.forEach(f => form.append('images', f));
    const resp = await this.http.post(`${this.basePath}/products/${productId}/assets`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return resp.data;
  }

  async updateProductAsset(assetId: string, body: { caption?: string; sortOrder?: number }): Promise<{ updated: boolean }> {
    await this._getAccessToken();
    const resp = await this.http.put(`${this.basePath}/product-assets/${assetId}`, body);
    return resp.data;
  }

  async deleteProductAsset(assetId: string): Promise<{ deleted: boolean }> {
    await this._getAccessToken();
    const resp = await this.http.delete(`${this.basePath}/product-assets/${assetId}`);
    return resp.data;
  }

  // ── Generation ──

  /** Selling points from the product's brief; 404 → null (no brief yet). */
  async getSellingPoints(productId: number, refresh = false): Promise<{ sellingPoints: string[] } | null> {
    await this._getAccessToken();
    try {
      const resp = await this.http.get(`${this.basePath}/products/${productId}/selling-points`,
        refresh ? { params: { refresh: '1' } } : undefined);
      return resp.data;
    } catch (err: any) {
      if (err?.response?.status === 404) return null;
      throw err;
    }
  }

  async getActivity(): Promise<{ analyzing: number; generating: number }> {
    await this._getAccessToken();
    const resp = await this.http.get(`${this.basePath}/activity`);
    return resp.data;
  }

  // ── Layout formats ──
  // Reads swallow to null via handleHttpRequest; writes propagate. House rule,
  // and listStaticFormats now follows it — its predecessor threw, which is why
  // both of its callers had to .catch() it.

  /** Every format: built-ins with their overrides applied, plus custom ones. */
  async listStaticFormats(): Promise<{ formats: StaticFormat[] } | null> {
    await this._getAccessToken();
    const data = await this.handleHttpRequest<StaticFormat[] | { formats: StaticFormat[] }>(
      () => this.http.get(`${this.basePath}/formats`),
      'Error listing static formats',
    );
    // null means the request FAILED — distinct from an empty registry, which the
    // caller must be able to tell apart.
    if (!data) return null;
    // Tolerate both response shapes: a bare array or { formats: [...] }.
    return { formats: Array.isArray(data) ? data : data.formats || [] };
  }

  /** Create a custom format. The key is slugged server-side and is immutable. */
  async createStaticFormat(body: {
    label: string; family: string; defaultAspect: string;
    description: string; skeleton: string; slotHints: Record<string, string>;
  }): Promise<{ format: StaticFormat }> {
    await this._getAccessToken();
    const resp = await this.http.post(`${this.basePath}/formats`, body);
    return resp.data;
  }

  /**
   * Save a format's editable fields. Errors propagate — the 400 names the slots
   * that have no hint. A built-in rejects label/family/defaultAspect: its
   * identity is code-owned, and duplicating it is the way to get an editable copy.
   */
  async updateStaticFormat(
    key: string,
    body: {
      description: string; skeleton: string; slotHints: Record<string, string>;
      label?: string; family?: string; defaultAspect?: string;
    },
  ): Promise<{ format: StaticFormat }> {
    await this._getAccessToken();
    const resp = await this.http.put(`${this.basePath}/formats/${key}`, body);
    return resp.data;
  }

  /** Fork any format — built-in or custom — into a new custom one. */
  async duplicateStaticFormat(key: string, body: { label?: string } = {}): Promise<{ format: StaticFormat }> {
    await this._getAccessToken();
    const resp = await this.http.post(`${this.basePath}/formats/${key}/duplicate`, body);
    return resp.data;
  }

  /** Drop a built-in's override so it resolves from code again. */
  async resetStaticFormat(key: string): Promise<{ format: StaticFormat }> {
    await this._getAccessToken();
    const resp = await this.http.post(`${this.basePath}/formats/${key}/reset`, {});
    return resp.data;
  }

  /** Delete a CUSTOM format. A built-in 409s — reset it instead. */
  async deleteStaticFormat(key: string): Promise<{ deleted: boolean }> {
    await this._getAccessToken();
    const resp = await this.http.delete(`${this.basePath}/formats/${key}`);
    return resp.data;
  }

  // ── Structured generation ──

  /** Queue one structured run. No axis: the format IS the composition. */
  async createStructuredGeneration(body: {
    formatKey: string; productId: string; assetIds?: string[]; sellingPoints?: string[];
    brief?: string; aspectRatio?: string; groupId?: string;
    promptOverride?: string; buildSheet?: Record<string, unknown>;
  }): Promise<{ generationId: string; groundingWarning?: string | null }> {
    await this._getAccessToken();
    const resp = await this.http.post(`${this.basePath}/format-generations`, body);
    return resp.data;
  }

  /**
   * Stage 2F + prompt assembly without queueing. Same response shape as
   * generationPreview on purpose, so one preview step renders both frameworks.
   */
  async structuredPreview(body: {
    formatKey: string; productId: string; sellingPoints?: string[]; brief?: string;
    aspectRatio?: string; hasProductReferences?: boolean;
  }): Promise<{
    prompt: string; aspectRatio: string | null; buildSheet: Record<string, unknown>;
    unfilledSlots?: string[]; needsProductPhoto?: boolean; groundingWarning?: string | null;
    textElements?: TextElement[];
  }> {
    await this._getAccessToken();
    const resp = await this.http.post(`${this.basePath}/format-generations/preview`, body);
    return resp.data;
  }

  /** Runs stage 2 + prompt assembly without queueing — ~$0.10 and 30-60s. */
  async generationPreview(staticAdId: string, body: {
    productId?: string; sellingPoints?: string[]; remakeBrief?: string;
    similarity?: GenerationSimilarity;
    hasProductReferences?: boolean; textMode?: 'ai' | 'overlay';
  }): Promise<{
    prompt: string; aspectRatio: string | null; buildSheet: Record<string, unknown>;
    /** Slots stage 2 could not fill: the render will produce something, just not this ad. */
    unfilledSlots?: string[];
    /** The sheet expects a product photo; without references the render invents a product. */
    needsProductPhoto?: boolean;
    /** Every string the image will render, read back out of the sheet's TEXT ELEMENTS block. */
    textElements?: TextElement[];
  }> {
    await this._getAccessToken();
    const resp = await this.http.post(`${this.basePath}/ads/${staticAdId}/generation-preview`, body);
    return resp.data;
  }

  /** Re-roll: new generation from a finished one — same (or edited) prompt, same text layer. */
  async rerollGeneration(generationId: string, opts: { tweak?: string; prompt?: string; assetIds?: string[] } = {}): Promise<{ generationId: string }> {
    await this._getAccessToken();
    const resp = await this.http.post(`${this.basePath}/generations/${generationId}/reroll`, {
      tweak: opts.tweak || undefined,
      prompt: opts.prompt || undefined,
      assetIds: opts.assetIds,
    });
    return resp.data;
  }

  /**
   * The same creative in another shape: the finished image is reframed rather
   * than re-composed, and the pair is grouped so it publishes as ONE Notion row
   * carrying both sizes.
   */
  async resizeGeneration(generationId: string, aspectRatio: string): Promise<{ generationId: string; groupId: string }> {
    await this._getAccessToken();
    const resp = await this.http.post(`${this.basePath}/generations/${generationId}/resize`, { aspectRatio });
    return resp.data;
  }

  /** Make an already-rendered version the generation's current output. */
  async setOutputVersion(generationId: string, key: string): Promise<void> {
    await this._getAccessToken();
    await this.http.put(`${this.basePath}/generations/${generationId}/output-version`, { key });
  }

  /** Remove a non-current version from the history. */
  async deleteOutputVersion(generationId: string, key: string): Promise<void> {
    await this._getAccessToken();
    await this.http.delete(`${this.basePath}/generations/${generationId}/output-version`, { params: { key } });
  }

  async getCosts(): Promise<{
    balance: number | null;
    totals: { usdAllTime: number; usdWeek: number; creditsAllTime: number; creditsWeek: number };
    rows: Array<{
      createdAt: string; label: string; refId: string | null; model: string | null;
      tokensIn: number | null; tokensOut: number | null; costUsd: number | null; credits: number | null;
    }>;
  }> {
    await this._getAccessToken();
    const resp = await this.http.get(`${this.basePath}/costs`);
    return resp.data;
  }

  /** Upload a style-reference image for a text effect. */
  async uploadEffectRef(file: File): Promise<{ key: string; url: string | null }> {
    await this._getAccessToken();
    const form = new FormData();
    form.append('image', file);
    const resp = await this.http.post(`${this.basePath}/effect-refs`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
    return resp.data;
  }

  async getFonts(): Promise<{ builtin: string[]; custom: Array<{ fontId: string; family: string; url: string | null }> }> {
    await this._getAccessToken();
    const resp = await this.http.get(`${this.basePath}/fonts`);
    return resp.data;
  }

  /** Upload a .ttf/.otf; the server fc-scans the REAL family name. */
  async uploadFont(file: File): Promise<{ family: string }> {
    await this._getAccessToken();
    const form = new FormData();
    form.append('font', file);
    const resp = await this.http.post(`${this.basePath}/fonts`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
    return resp.data;
  }

  async saveAdCopy(generationId: string, body: { headlines: string[]; captions: string[] }): Promise<void> {
    await this._getAccessToken();
    await this.http.put(`${this.basePath}/generations/${generationId}/ad-copy`, body);
  }

  /** Same pipeline as the video hook generator: composed prompt in, raw text out. */
  async suggestAdCopy(generationId: string, body: { prompt: string; model?: string }): Promise<{ text: string; model: string; runId?: string; truncated?: boolean }> {
    await this._getAccessToken();
    const resp = await this.http.post(`${this.basePath}/generations/${generationId}/suggest-ad-copy`, body);
    return resp.data;
  }

  /** Past studio runs, newest first. Default scope is this generation's
   * source ad; scope 'product' widens to every creative of the same product. */
  async listAdCopyRuns(generationId: string, opts?: { scope?: 'product' }): Promise<{ runs: AdCopyRun[] }> {
    await this._getAccessToken();
    const resp = await this.http.get(`${this.basePath}/generations/${generationId}/ad-copy-runs`, {
      params: opts?.scope ? { scope: opts.scope } : {},
    });
    return resp.data;
  }

  /** Publish history for one output, newest first. */
  async listDeliveries(generationId: string): Promise<{ deliveries: StaticDelivery[] }> {
    await this._getAccessToken();
    const resp = await this.http.get(`${this.basePath}/generations/${generationId}/deliveries`);
    return resp.data;
  }

  /** Publish outputs: Dropbox upload + Notion row per item, sequential server-side.
   * A group item ({ groupId, generationIds, name }) publishes the whole group
   * under one name — files name-1…-N, one Notion row with the shared copy. */
  async batchFinalize(items: FinalizeItem[]): Promise<{ results: FinalizeResult[] }> {
    await this._getAccessToken();
    const resp = await this.http.post(`${this.basePath}/generations/batch-finalize`, { items });
    return resp.data;
  }

  // ── Generation groups (writes — errors propagate) ──
  //
  // Merge rules live server-side: creating or adding with members that are
  // already grouped absorbs everything into the existing/oldest group, so the
  // client never has to reconcile overlapping groups itself.

  /** New group, optionally seeded with members. */
  async createGenerationGroup(generationIds?: string[]): Promise<{ groupId: string; memberCount: number }> {
    await this._getAccessToken();
    const resp = await this.http.post(`${this.basePath}/generation-groups`, { generationIds });
    return resp.data;
  }

  async addGroupMember(groupId: string, generationId: string): Promise<{ groupId: string; memberCount: number }> {
    await this._getAccessToken();
    const resp = await this.http.post(`${this.basePath}/generation-groups/${groupId}/members`, { generationId });
    return resp.data;
  }

  /** `dissolved` = the group fell under 2 members and was deleted server-side. */
  async removeGroupMember(groupId: string, generationId: string): Promise<{ removed: boolean; dissolved: boolean }> {
    await this._getAccessToken();
    const resp = await this.http.delete(`${this.basePath}/generation-groups/${groupId}/members/${generationId}`);
    return resp.data;
  }

  /** Ungroup: the members survive as loose generations. */
  async deleteGenerationGroup(groupId: string): Promise<{ deleted: boolean }> {
    await this._getAccessToken();
    const resp = await this.http.delete(`${this.basePath}/generation-groups/${groupId}`);
    return resp.data;
  }

  /** Shared launch copy for every member — the group's one source of truth. */
  async saveGroupAdCopy(
    groupId: string,
    body: { headlines: string[]; captions: string[] },
  ): Promise<{ adCopy: GroupInfo['adCopy'] }> {
    await this._getAccessToken();
    const resp = await this.http.put(`${this.basePath}/generation-groups/${groupId}/ad-copy`, body);
    return resp.data;
  }

  async deleteAdCopyRun(generationId: string, runId: string): Promise<void> {
    await this._getAccessToken();
    await this.http.delete(`${this.basePath}/generations/${generationId}/ad-copy-runs/${runId}`);
  }

  // ── Inspiration sets (the creative agent's daily ad sets) ──

  async listInspoSets(limit = 6): Promise<{ sets: InspoSet[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/inspo-sets`, { params: { limit } }),
      'Error loading daily inspiration sets',
    );
  }

  /** The set's accepted images + captions.txt, as one zip. */
  async getInspoSetZip(setId: string): Promise<Blob> {
    await this._getAccessToken();
    const resp = await this.http.get(`${this.basePath}/inspo-sets/${setId}/zip`, { responseType: 'blob' });
    return resp.data;
  }

  /** The plate's raw bytes via the API (CORS-safe for canvas compositing). */
  async getPlateBlob(generationId: string): Promise<Blob> {
    await this._getAccessToken();
    const resp = await this.http.get(`${this.basePath}/generations/${generationId}/plate`, { responseType: 'blob' });
    return resp.data;
  }

  /** WYSIWYG save: upload the client-rendered composite plus its spec. */
  async uploadOverlayRender(generationId: string, image: Blob, spec: { elements: unknown[] }): Promise<{ outputImageUrl: string }> {
    await this._getAccessToken();
    const form = new FormData();
    form.append('image', image, 'render.jpg');
    form.append('spec', JSON.stringify(spec));
    const resp = await this.http.put(`${this.basePath}/generations/${generationId}/overlay-render`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return resp.data;
  }

  /** Post-render image pass applying the elements' effects. Spends image credits. */
  async applyEffects(generationId: string): Promise<{ outputImageUrl: string }> {
    await this._getAccessToken();
    const resp = await this.http.post(`${this.basePath}/generations/${generationId}/apply-effects`, {});
    return resp.data;
  }

  /** Re-flatten an edited overlay spec onto the stored plate (server-side). */
  async updateOverlay(generationId: string, spec: { elements: unknown[] }): Promise<{ outputImageUrl: string }> {
    await this._getAccessToken();
    const resp = await this.http.put(`${this.basePath}/generations/${generationId}/overlay`, { spec });
    return resp.data;
  }

  async createGeneration(body: {
    staticAdId: string; productId?: string; assetIds?: string[]; sellingPoints?: string[]; remakeBrief?: string;
    similarity?: GenerationSimilarity; promptOverride?: string; aspectRatio?: string | null;
    buildSheet?: Record<string, unknown>; textMode?: 'ai' | 'overlay'; groupId?: string;
  }): Promise<{ generationId: string }> {
    await this._getAccessToken();
    const resp = await this.http.post(`${this.basePath}/generations`, body);
    return resp.data;
  }

  // `total` counts CARDS (a group = one card) and groups never split across
  // pages, so the pager and the folded grid agree.
  async listGenerations(
    filters: {
      productId?: string; staticAdId?: string; status?: string; excludeFailed?: string;
      tag?: string[];
    } = {},
    { limit = 60, offset = 0 }: { limit?: number; offset?: number } = {},
  ): Promise<{ generations: GenerationRow[]; groups?: Record<string, GroupInfo>; total: number } | null> {
    await this._getAccessToken();
    // Arrays stay arrays, same as listAds: axios sends repeated tag[]= params
    // and Express reads them back as one array. The old truthy-only loop would
    // have coerced ['a','b'] into the single literal tag "a,b".
    const params: Record<string, string | number | string[]> = { limit, offset };
    Object.entries(filters).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      if (Array.isArray(value) && !value.length) return;
      params[key] = value as string | number | string[];
    });
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/generations`, { params }),
      'Error listing generations',
    );
  }

  async getGenerationStatuses(ids: string[]): Promise<{ statuses: GenerationStatusRow[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/generations/status`, { params: { ids: ids.join(',') } }),
      'Error polling generations',
    );
  }

  async retryGeneration(generationId: string): Promise<{ status: string }> {
    await this._getAccessToken();
    const resp = await this.http.post(`${this.basePath}/generations/${generationId}/retry`);
    return resp.data;
  }

  async deleteGeneration(generationId: string): Promise<{ deleted: boolean }> {
    await this._getAccessToken();
    const resp = await this.http.delete(`${this.basePath}/generations/${generationId}`);
    return resp.data;
  }


}

export default StaticAdsService;
